import type { PlanCalibration } from '../types';
import { planLocalToWorld, worldToPlanLocal, type PlanRect } from '../planGeometry';

export interface Pt {
  x: number;
  y: number;
}

export type Rect = PlanRect;

export interface Metrics {
  uvToSource(u: number, v: number): Pt;
  sourceToUv(p: Pt): { u: number; v: number };
  worldToSource(p: Pt, rect: Rect): Pt;
  sourceToWorld(p: Pt, rect: Rect): Pt;

  toMm(px: number): number | null;
  distanceMm(a: Pt, b: Pt): number | null;

  setCalibration(c: PlanCalibration): void;
  getCalibration(): PlanCalibration | null;
  isStale(currentBackgroundHash: string): boolean;
}

export function makeUserCalibration(args: {
  a: Pt;
  b: Pt;
  refLengthMm: number;
  backgroundHash: string;
  createdAt: string;
}): PlanCalibration {
  const segmentLenSourcePx = Math.hypot(args.b.x - args.a.x, args.b.y - args.a.y);
  return {
    pxPerMm: segmentLenSourcePx / args.refLengthMm,
    refSegmentSourcePx: {
      x1: args.a.x,
      y1: args.a.y,
      x2: args.b.x,
      y2: args.b.y,
    },
    refLengthMm: args.refLengthMm,
    backgroundHash: args.backgroundHash,
    confidence: 'user',
    createdAt: args.createdAt,
  };
}

export function createMetrics(args: { naturalWidth: number; naturalHeight: number }): Metrics {
  let calibration: PlanCalibration | null = null;

  return {
    uvToSource: (u, v) => ({
      x: u * args.naturalWidth,
      y: v * args.naturalHeight,
    }),
    sourceToUv: p => ({
      u: args.naturalWidth === 0 ? 0 : p.x / args.naturalWidth,
      v: args.naturalHeight === 0 ? 0 : p.y / args.naturalHeight,
    }),
    worldToSource: (p, rect) => {
      const { u, v } = worldToPlanLocal(p, rect);
      return { x: u * args.naturalWidth, y: v * args.naturalHeight };
    },
    sourceToWorld: (p, rect) => planLocalToWorld({
      u: args.naturalWidth === 0 ? 0 : p.x / args.naturalWidth,
      v: args.naturalHeight === 0 ? 0 : p.y / args.naturalHeight,
    }, rect),
    toMm: px => calibration === null ? null : px / calibration.pxPerMm,
    distanceMm: (a, b) => {
      if (calibration === null) return null;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      return Math.hypot(dx, dy) / calibration.pxPerMm;
    },
    setCalibration: c => { calibration = c; },
    getCalibration: () => calibration,
    isStale: currentBackgroundHash => calibration !== null && calibration.backgroundHash !== currentBackgroundHash,
  };
}

export function computeBackgroundHash(pngDataUrl: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < pngDataUrl.length; i += 1) {
    hash ^= pngDataUrl.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}
